import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@material-ui/core';
import useForm from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import FormSection from '../../../../components/FormComponents/FormSection';
import ChangeApplicant from '../../../../components/ClaimFormComponents/ChangeApplicant';
import OtherPhysicalApplicantForm
from '../../components/OtherPhysicalApplicantForm';
import compensationSchema, { keys } from '../../../../validations/claimValidationSchema/compensationSchema';
import OtherLegalApplicantForm from '../../../../components/FormComponents/OtherLegalApplicantForm';
import BankAccountForm from '../../../../components/FormComponents/BankAccountForm/BankAccountForm';
import { TRANSFER_TYPES_WITHOUT_CASH } from '../../../../constants';
import { selectSortedCountries } from '../../../../redux/modules/countries';
import { filterPaymentReceiverTypes } from '../../../../helpers/claims';
import { getBanks } from '../../../../redux/modules/claimApplication';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import { APPLICANT_TYPES } from '../../../../constants/claim';

import PaymentReceiverIcon from '../../../../img/formSectionIcons/paymentReceiver.svg';
import styles from '../steps.module.scss';

const DEFAULT_VALUES = {
  citizenship: null,
};

const CompensationStep = ({
  step,
  goNext,
  goPrevious,
  setStepNavigationBox,
  claim,
  applicantIsAdult,
  formLanguage,
  updateForm,
  transferTypes,
  applicantPrimaryOption,
}) => {
  const { compensation } = claim;

  const { i18n } = useTranslation();
  const lang = i18n.language.toUpperCase();
  const isDeviceLgSize = useDeviceResize('lg');
  const dispatch = useDispatch();

  const banks = useSelector((state) => state.claimApplication.banks);
  const countries = useSelector((state) => selectSortedCountries(state, lang));
  const paymentReceiverTypes = useMemo(() => filterPaymentReceiverTypes(compensation), [
    compensation,
  ]);

  const [paymentReceiverType, setPaymentReceiverType] = useState(
    compensation.paymentReceiverType || paymentReceiverTypes[applicantIsAdult ? 0 : 1].value,
  );
  useEffect(() => {
    dispatch(getBanks());
  }, [dispatch]);

  const {
    handleSubmit,
    register,
    setValue,
    errors,
    clearError,
    unregister,
    getValues,
    triggerValidation,
  } = useForm({
    validationSchema: compensationSchema,
    mode: 'onBlur',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...compensation,
      formLanguage,
    },
  });

  useRegisterValidationFields(['paymentReceiverType', 'citizenship', 'formLanguage'], register);

  useEffect(() => {
    setValue('paymentReceiverType', paymentReceiverType);
  }, [setValue, paymentReceiverType]);

  useEffect(() => {
    setValue('formLanguage', formLanguage);
  }, [setValue, formLanguage]);

  const onSubmit = (data) => {
    dispatch(updateForm({ name: 'compensation', data }));
    dispatch(
      updateForm({
        name: 'attachments',
        data: {
          isOtherPaymentReceiver: ['other', 'otherLegal'].includes(data.paymentReceiverType),
        },
      }),
    );
    goNext(step);
  };

  return (
    <Box className={styles.formContainer}>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
      />
      <FormSection
        title="claim.compensation.paymentReceiverLabel"
        icon={PaymentReceiverIcon}
      >
        <ChangeApplicant
          primaryOption={applicantPrimaryOption}
          value={paymentReceiverType}
          onChange={(value) => {
            setPaymentReceiverType(value);
            setValue('paymentReceiverType', value);
          }}
          applicantIsAdult={applicantIsAdult}
          hasApplicantOption={compensation.isOtherApplicant}
        />
        {paymentReceiverType === APPLICANT_TYPES[1].value && (
        <OtherPhysicalApplicantForm
          initialData={compensation}
          valueNames={keys}
          formProps={{
            errors,
            clearError,
            getValues,
            setValue,
            register,
            unregister,
            triggerValidation,
          }}
          countries={countries}
        />
        )}
        {paymentReceiverType === APPLICANT_TYPES[2].value && (
        <OtherLegalApplicantForm
          initialData={compensation}
          formProps={{
            register,
            setValue,
            errors,
            clearError,
            triggerValidation,
          }}
          valueNames={{
            hvhh: 'paymentReceiverHvhh',
            organizationName: `paymentReceiverName${formLanguage.toUpperCase()}`,
            email: 'paymentReceiverEmail',
            phone: 'paymentReceiverPhone',
          }}
        />
        )}
      </FormSection>
      <BankAccountForm
        formProps={{
          register,
          setValue,
          clearError,
          errors,
          unregister,
        }}
        transferTypes={transferTypes}
        banks={banks}
        initialData={compensation}
      />
    </Box>
  );
};

CompensationStep.propTypes = {
  step: PropTypes.shape({
    n: PropTypes.number.isRequired,
  }).isRequired,
  goNext: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  applicantIsAdult: PropTypes.bool.isRequired,
  claim: PropTypes.shape({
    compensation: PropTypes.shape({
      isOtherApplicant: PropTypes.bool.isRequired,
      paymentReceiverType: PropTypes.string,
    }),
  }).isRequired,
  formLanguage: PropTypes.string.isRequired,
  updateForm: PropTypes.func.isRequired,
  transferTypes: PropTypes.arrayOf(PropTypes.shape({})),
  applicantPrimaryOption: PropTypes.shape({
    primaryLabel: PropTypes.string,
    primaryValue: PropTypes.string,
  }),
};

CompensationStep.defaultProps = {
  transferTypes: TRANSFER_TYPES_WITHOUT_CASH,
  applicantPrimaryOption: {},
};

export default CompensationStep;
